import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { services as serviceData } from '../utils/serviceData';

export default function Services() {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(null);
  const [hoveredReadMore, setHoveredReadMore] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef(null);
  const hubRef = useRef(null);
  const headerRefs = useRef([]);
  const pathsRef = useRef([]);
  const accordionRefs = useRef([]);
  const contentRefs = useRef([]);
  

  // Check if mobile screen
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Initial check
    checkIfMobile();
    
    // Listen for resize events
    window.addEventListener('resize', checkIfMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Initialize refs arrays
  useEffect(() => {
    headerRefs.current = headerRefs.current.slice(0, serviceData.length);
    pathsRef.current = pathsRef.current.slice(0, serviceData.length);
    accordionRefs.current = accordionRefs.current.slice(0, serviceData.length);
    contentRefs.current = contentRefs.current.slice(0, serviceData.length);
  }, [serviceData.length]);

  // Calculate and update paths
  const updatePaths = () => {
    if (!containerRef.current) return;
    
    const containerRect = containerRef.current.getBoundingClientRect();
    
    serviceData.forEach((_, index) => {
      const headerElement = headerRefs.current[index];
      const path = pathsRef.current[index];
      const titleElement = headerElement?.querySelector('.service-title');
      
      if (!headerElement || !path) return;
      
      const headerRect = headerElement.getBoundingClientRect();
      
      // Get the title element's position if available (for mobile)
      let endX, endY;
      if (isMobile && titleElement) {
        const titleRect = titleElement.getBoundingClientRect();
        // Connect to the left side of the text itself
        endX = titleRect.left - containerRect.left;
        endY = titleRect.top + titleRect.height / 2 - containerRect.top;
      } else {
        // Default: connect to the left side of the header container
        endX = headerRect.left - containerRect.left;
        endY = headerRect.top + headerRect.height / 2 - containerRect.top;
      }
      
      let startX, startY, controlX1, controlY1, controlX2, controlY2;
      
      if (isMobile) {
        // For mobile: Calculate vertical position for the lines
        const mobileHubCenterY = containerRect.height / 2;
        const startYBase = mobileHubCenterY - ((serviceData.length - 1) * 15) / 2;
        
        // Start from left side of screen with distributed vertical positions
        startX = 0;
        startY = startYBase + (index * 15);
        
        // Calculate mid point for a nice curve
        const midX = startX + (endX - startX) * 0.6;
        
        // Have the line go horizontally first, then curve gently to the destination
        controlX1 = midX;
        controlY1 = startY;
        controlX2 = midX;
        controlY2 = endY;
      } else {
        // For desktop: Use the actual hub box
        if (!hubRef.current) return;
        const hubRect = hubRef.current.getBoundingClientRect();
        
        // Calculate vertical position based on fixed intervals
        const hubCenterY = hubRect.top + hubRect.height / 2 - containerRect.top;
        const startYBase = hubCenterY - ((serviceData.length - 1) * 15) / 2;
        startY = startYBase + (index * 15);
        
        // Fixed starting point (right side of Services box)
        startX = hubRect.right - containerRect.left;
        
        // Calculate mid point for a nice curve
        const midX = startX + (endX - startX) * 0.6;
        
        // Have the line go horizontally first, then curve gently to the destination
        controlX1 = midX;
        controlY1 = startY;
        controlX2 = midX;
        controlY2 = endY;
      }
      
      // Update path with gentle curves
      const pathData = `M ${startX} ${startY} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${endX} ${endY}`;
      path.setAttribute('d', pathData);
    });
  };

  // Create a continuously running animation frame loop for smooth path updates
  useEffect(() => {
    let frameId;
    let running = true;
    
    const updateFrame = () => {
      if (running) {
        updatePaths();
        frameId = requestAnimationFrame(updateFrame);
      }
    };
    
    // Start the animation frame loop
    frameId = requestAnimationFrame(updateFrame);
    
    // Clean up
    return () => {
      running = false;
      cancelAnimationFrame(frameId);
    };
  }, [isMobile]);

  // Toggle accordion
  const toggleAccordion = (index) => {
    setActiveIndex(prevIndex => prevIndex === index ? null : index);
  };

  // Ensure opened dropdown is fully visible on mobile
  useEffect(() => {
    if (!isMobile || activeIndex === null) return;

    const contentEl = contentRefs.current[activeIndex];
    if (!contentEl) return;

    // Get the position of the content element
    const rect = contentEl.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    
    // Calculate the center position of the expanded content
    const contentCenter = rect.top + (rect.height / 2);
    const viewportCenter = viewportHeight / 2;
    
    // Calculate how much we need to scroll to center the content
    const scrollAmount = contentCenter - viewportCenter;
    
    // Add a small offset to ensure some padding at the top
    const finalScrollAmount = scrollAmount - 100;
    
    // Smooth scroll to the calculated position
    window.scrollBy({
      top: finalScrollAmount,
      behavior: 'smooth'
    });
  }, [activeIndex, isMobile]);

  return (
    <div className="relative w-full max-w-6xl mx-auto p-4 flex items-center min-h-screen" ref={containerRef}>
      {/* SVG Container for paths */}
      <div className="absolute inset-0 pointer-events-none">
        <svg className="w-full h-full" preserveAspectRatio="none">
          <defs>
            <style>
              {`
                path {
                  transition: stroke 500ms ease-in-out, stroke-width 500ms ease-in-out;
                }
              `}
            </style>
          </defs>
          {serviceData.map((_, index) => (
            <path
              key={index}
              ref={el => pathsRef.current[index] = el}
              className={`fill-none ${
                activeIndex === index ? 'stroke-emerald-800 stroke-[3]' : 'stroke-black stroke-[2]'
              }`}
            />
          ))}
        </svg>
      </div>
      
      {/* Services Hub - Only show on desktop */}
      {!isMobile && (
        <div 
          ref={hubRef}
          className="absolute left-0 top-1/2 -translate-y-1/2 w-48 h-48 bg-green-900 text-white flex justify-center items-center text-2xl font-bold z-10"
        >
          <span className="text-center">{t('services')}</span>
        </div>
      )}
      
      {/* Mobile Services Title - Centered on the screen */}
      {isMobile && (
        <div 
          className={`absolute transition-all duration-500 ease-in-out z-10 ${
            activeIndex !== null ? 'top-16' : 'top-20'
          } left-0 right-0 text-center`}
        >
          <h2 className="text-xl font-bold text-emerald-800 inline-block">{t('services')}</h2>
        </div>
      )}
      
      {/* Services Menu - Adjust margin based on screen size */}
      <div className={`${isMobile ? 'ml-6 mt-24' : 'ml-64'} w-full md:w-3/4`}>
        {serviceData.map((service, index) => (
          <div
            key={index}
            className="mb-4"
            ref={el => accordionRefs.current[index] = el}
          >
            <div 
              ref={el => headerRefs.current[index] = el}
              className={`service-header flex justify-between items-center cursor-pointer py-4 border-b border-gray-200 transition-colors duration-300 ${
                activeIndex === index ? 'text-emerald-800' : ''
              }`}
              onClick={() => toggleAccordion(index)}
            >
              <span 
                className={`service-title ${isMobile ? 'text-base md:text-xl' : 'text-xl'} font-bold truncate max-w-[100%]`}
              >
                {t(service.titleKey)}
              </span>
              <span className={`text-2xl md:text-3xl transition-transform duration-300 ${
                activeIndex === index ? 'transform rotate-45 text-emerald-800' : 'text-emerald-800'
              }`}>{activeIndex === index ? '+' : '+'}</span>
            </div>
            
            {/* Content panel - slides down when active */}
            <div
              ref={el => contentRefs.current[index] = el}
              className={`transition-all duration-500 ease-in-out overflow-hidden ${
                activeIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="py-4 px-4 border-b border-gray-200 text-left">
                <p className="leading-relaxed">{t(service.contentKey)}</p>
                <Link
                  to={`/services/${service.slug}`}
                  className="relative overflow-hidden transition-all duration-300 inline-block mt-4"
                  onMouseEnter={() => setHoveredReadMore(index)}
                  onMouseLeave={() => setHoveredReadMore(null)}
                  style={{ fontFamily: "'Space Mono', monospace" }}
                >
                  <span className="relative z-10 text-white text-base px-5 py-2 inline-block">
                    {t('read_more')}
                  </span>
                  <span
                    className="absolute inset-0 bg-green-900 hover:bg-green-700 transition-all duration-300"
                    style={{
                      transform: hoveredReadMore === index ? 'scale(0.8)' : 'scale(1)',
                      transition: 'transform 0.3s ease'
                    }}
                  ></span>
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}